import { Directive, Input, OnInit, inject } from '@angular/core';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import cloneDeep from 'lodash/cloneDeep';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisArrayUtils } from '../../core/utils';
import { TreeNodeData } from '../../tree/model/tree-node-data';
import { HisSearchCriteriagroup } from '../model/his-search-criteriagroup';
import { HisWidgetBase } from '../model/his-widget-base';
import { HisWidgetGroup } from '../model/his-widget-group';
import { HisMultiSelectWidget } from '../model/his-widget-multiselect';
import { HisSearchSelectWidget } from '../model/his-widget-search-select';
import { HisSelectWidget } from '../model/his-widget-select';
import { HisTreeSelectWidget } from '../model/his-widget-tree-select';
import { HisSearchService } from '../service/his-search.service';
import { HisWidgetService } from '../service/his-widget.service';

@Directive()
export class HisSearchCriteriagroupComponentBase implements OnInit {
    private hisUiService = inject(HisUiService);
    private notificationService = inject(HisNotificationService);
    private widgetService = inject(HisWidgetService);
    private searchService = inject(HisSearchService);


    @Input() group: HisWidgetGroup;
    @Input() form: UntypedFormGroup;
    @Input() widgetGroups: HisWidgetGroup[];

    currentlyEditedIndex: number;
    currentCriteriagroup: HisSearchCriteriagroup;
    criteriagroupOverlayVisible = false;
    isEditMode = false;
    isAddMode = false;

    criteriaGroupToRestore: HisSearchCriteriagroup;

    ngOnInit() {
        console.log( 'combineCriteriaGroupsByOr #init', this.group.combineCriteriaGroupsByOr );
        this.initializeResetInputs();
        this.setResetInputs( false );
    }

    addCriteriaGroup( e: Event ): void {
        this.isAddMode = true;
        // create new ( temporary ) criteria group which will hold the data for the overlay
        this.currentCriteriagroup = new HisSearchCriteriagroup();

        // Aus irgendeinem Grund wird combineCriteriaGroupsByOr nach Suche ändern
        // beim Hinzufügen einer 2. Kriteriengruppe bei #addCriteriaGroup() true
        // Siehe Hiszilla 257845
        if ( this.group.criteriaGroups.length < 2 ) {
            // noch mal ausdrücklich auf default Wert setzen
            this.group.combineCriteriaGroupsByOr = false;
        }

        console.log( 'combineCriteriaGroupsByOr #add', this.group.combineCriteriaGroupsByOr );

        // add if new criteria group
        if ( this.currentlyEditedIndex != null ) {
            console.log(
                'his-search-criteriagroup.component#saveCriteriaGroup() #this.currentCriteriagroup already exists:',
                this.currentCriteriagroup );
            this.group.criteriaGroups[this.currentlyEditedIndex] = this.currentCriteriagroup;
        } else {
            let index = String( this.group.criteriaGroups.length );

            for ( let widget of this.group.widgets ) {
                if (widget.id.includes('IGNORE:multi')) {
                    // excludes multi search fields from criteria group #hiszilla 326976
                    continue;
                }
				
                let widgetCopy = this.widgetService.copyWidget( widget, this.group.label + '[' + index + ']' + widget.id );
                console.log( 'his-search-criteriagroup.component#addCriteriaGroup #widgetCopy', widgetCopy );

                // eventuell vorhandene Werte dürfen nicht in der Kriteriengruppe angezeit werden
                widgetCopy.value = undefined;

                this.currentCriteriagroup.widgets.push( widgetCopy );
            }
            let groupLabel = this.group.label;
            console.log( 'his-search-criteriagroup.component#addCriteriaGroup #groupLabel', groupLabel );

            // ohne das Hinzufügen an dieser Stelle wird die Kritieriengruppe nicht angezeigt
            this.addCriteriaGroupToForm( this.currentCriteriagroup, groupLabel );
        }

        // show overlay
        this.showCriteriagroupOverlay();

        e.preventDefault();
    }

    removeCriteriaGroup( e: Event, criteriagroup: HisSearchCriteriagroup ): void {
        //this.group.criteriaGroups = this.group.criteriaGroups.filter(element => element !== criteriagroup);
        this.remove( criteriagroup );
        e.preventDefault();
    }

    private remove( criteriagroup: HisSearchCriteriagroup ) {
        this.group.criteriaGroups = this.group.criteriaGroups.filter( element => element !== criteriagroup );
    }

    editCriteriaGroup( e: Event, criteriagroup: HisSearchCriteriagroup ): void {
        console.log( 'his-search-criteriagroup.component#editCriteriaGroup() #criteriagroup', criteriagroup );
        this.currentCriteriagroup = criteriagroup;
        this.criteriaGroupToRestore = cloneDeep( criteriagroup );
        this.currentlyEditedIndex = this.group.criteriaGroups.indexOf( criteriagroup );
        this.showCriteriagroupOverlay();
        this.isEditMode = true;
        e.preventDefault();
    }

    private showCriteriagroupOverlay(): void {
        this.criteriagroupOverlayVisible = true;
    }

    private hideCriteriagroupOverlayAfterCancel(): void {
        this.criteriagroupOverlayVisible = false;
        console.log( 'his-search-criteriagroup.component#hideCriteriagroupOverlayAfterCancel()' );

        this.currentCriteriagroup = null;
        this.currentlyEditedIndex = null;
    }

    private hideCriteriagroupOverlayAfterSaved(): void {
        this.criteriagroupOverlayVisible = false;
        this.currentCriteriagroup = null;
        this.currentlyEditedIndex = null;
    }

    saveCriteriaGroup( e: Event ): void {
        e.preventDefault();

        if ( !this.atLeastOneValueSet() ) {
            this.notificationService.clear();
            this.notificationService.error(
                this.hisUiService.translate( 'cs.sys.genericSearch.criteriaGroup.notices.missingInputField.summary' ),
                this.hisUiService.translate( 'view.messages.error' ) );
            return;
        }
        console.log( 'his-search-criteriagroup.component#saveCriteriaGroup() #this.group', this.group );
        //If an error was previously generated, it will now be deleted if the input is valid. Hiszilla#278609
        this.notificationService.clear();

        this.isEditMode = false;
        this.isAddMode = false;
        this.hideCriteriagroupOverlayAfterSaved();
    }

    private atLeastOneValueSet(): boolean {
        for ( let widget of this.currentCriteriagroup.widgets ) {
            if ( this.widgetValueAsString( widget ) ) {
                return true;
            }
        }

        return false;
    }

    cancelEditCriteriaGroup( e: Event ): void {
        this.notificationService.clear();

        //console.log('his-search-criteriagroup.component#cancelEditCriteriaGroup #this.isAddMode', this.isAddMode);
        //console.log('his-search-criteriagroup.component#cancelEditCriteriaGroup #this.isEditMode', this.isEditMode);
        if ( this.isAddMode ) {
            // die hinzugefügte Kriteriengruppe muss wieder entfernt werden
            this.remove( this.currentCriteriagroup );

            this.currentCriteriagroup = undefined;
            this.isAddMode = false;
        }

        if ( this.isEditMode && this.criteriaGroupToRestore ) {
            //console.log('his-search-criteriagroup.component#cancelEditCriteriaGroup #currentlyEditedIndex', this.currentlyEditedIndex);
            //console.log('his-search-criteriagroup.component#cancelEditCriteriaGroup #this.groupToRestore', this.criteriaGroupToRestore);
            this.group.criteriaGroups[this.currentlyEditedIndex] = this.criteriaGroupToRestore;

            this.currentCriteriagroup = undefined;
            this.criteriaGroupToRestore = undefined;
            this.isEditMode = false;
        }

        this.hideCriteriagroupOverlayAfterCancel();
        if ( Object.keys( e ).length !== 0 ) {
            e.preventDefault();
        }
    }

    widgetValueAsString( widget: HisWidgetBase<any> ): string {
        if ( widget.value == null ) {
            return '';
        }

        // console.log('his-search-criteriagroup.component#widgetValueAsString #widget', widget);

        switch ( widget.kind ) {
            case 'dropdown': {
                let selectWidget = widget as HisSelectWidget;
                let label = selectWidget.value.label;
                return this.addOperdatorPrefixNotIfPresent( selectWidget.not, label );
            }
            case 'multi-dropdown': {
                let multiSelectWidget = widget as HisMultiSelectWidget;
                let label = multiSelectWidget.value
                    .map( option => option.label )
                    .join( ', ' );
                return this.addOperdatorPrefixNotIfPresent( multiSelectWidget.not, label );
            }
            case 'tree-select': {
                let treeSelectWidget = widget as HisTreeSelectWidget;
                let label = widget.value
                    .map( ( treeNodeData: TreeNodeData ) => {
                        let defaulttext = treeNodeData.defaulttext;
                        let subTreeSelected = treeNodeData.subTreeSelected;
                        if ( !subTreeSelected ) {
                            return defaulttext;
                        }

                        return defaulttext + ' (' + this.hisUiService.translate( 'genericSearch.tree.includeSubNodes' ) + ')';
                    } )
                    .join( ', ' );
                return this.addOperdatorPrefixNotIfPresent( treeSelectWidget.not, label );
            }
            case 'search-select': {
                let searchSelectWidget = widget as HisSearchSelectWidget;
                let label = searchSelectWidget.value.map( option => option.label ).join( ', ' );
                return this.addOperdatorPrefixNotIfPresent( searchSelectWidget.not, label );
            }
            case 'checkbox': {
                if ( widget.value ) {
                    return this.hisUiService.translate( 'de.his.javang.ja' );
                }

                return '';
            }
            case 'calendar': {
                // as of 2020-09-24 the kind 'calendar' is not used
                return widget.value;
            }
            default: {
                return widget.value;
            }
        }
    }

    private addOperdatorPrefixNotIfPresent( not: boolean, label: string ): any {
        if ( !not ) {
            return label;
        }
        return this.hisUiService.translate( 'genericSearch.result.notLabel' ) + ' ' + label;
    }

    // Eingaben zurücksetzen
    initializeResetInputs() {
        this.searchService.resetInputs.subscribe( ( resetInputs: boolean ) => {
            if ( resetInputs ) {
                this.group.criteriaGroups = [];
            }
        } );
    }

    setResetInputs( resetInputs: boolean ) {
        this.searchService.setResetInputs( resetInputs );
    }

    addCriteriaGroupToForm( criteriaGroup: HisSearchCriteriagroup, groupLabel: string ) {
        console.log( 'his-search-criteriagroup.component#addCriteriaGroupToForm() #criteriaGroup', criteriaGroup );
        let groups: HisWidgetGroup[] = this.widgetGroups;
        console.log( 'his-search-criteriagroup.component#addCriteriaGroupToForm() #groups', groups );
        for ( let group of groups ) {
            console.log(
                'his-search-criteriagroup.component#addCriteriaGroupToForm() #group.label', group.label, '#groupLabel', groupLabel );
            if ( group.label === groupLabel ) {
                group.criteriaGroups.push( criteriaGroup );
            }
        }
        console.log( 'his-search-criteriagroup.component#addCriteriaGroupToForm() #groups', groups );
        this.updateForm( groups );
    }

    updateForm( groups: HisWidgetGroup[] ) {
        let formGroup: any = {};
        for ( let widgetGroup of groups ) {
            for ( let widget of widgetGroup.widgets ) {
                formGroup[widget.id] = new UntypedFormControl( widget.value );
                // this.form.addControl(widget.id, new FormControl( widget.value ));
                console.log( 'his-search-criteriagroup.component#updateForm() #formGroup', formGroup );
            }
            for ( let criteriaGroup of widgetGroup.criteriaGroups ) {
                for ( let criteriaGroupWidget of criteriaGroup.widgets ) {
                    console.log( 'his-search-criteriagroup.component#updateForm() #criteriaGroupWidget', criteriaGroupWidget );
                    formGroup[criteriaGroupWidget.id] = new UntypedFormControl( criteriaGroupWidget.value );
                }
                console.log( 'his-search-criteriagroup.component#updateForm() #added criteriaGroup', criteriaGroup );
            }
        }

        // das DOM wird neu aufgebaut, deshalb kann hier form auch neu gefüllt werden
        // mit addControll würden die Werte im Fomular gelöscht werden
        this.form = new UntypedFormGroup( formGroup );
    }

    isValueToShow( value: any ) {
        if ( !value ) {
            return false;
        }
        if ( HisArrayUtils.isEmptyArray( value ) ) {
            console.log( 'his-search-criteriagroup.component#isValueToShow #value', value );
            return false;
        }
        return true;
    }

}
